import { useForm, useWatch } from 'react-hook-form';
import { useAppSelector } from 'store/index';
import { selectCompany, selectCompanyId } from 'store/selectors/me/company';
import { SettingsMain, SettingsPageHeader } from '../SettingsLayout';
import CommerceTypeAvatar from 'storybookConfig/stories/cells/Avatar/CommerceTypeAvatar';
import Form from 'storybookConfig/stories/organisms/Form';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import { useMutation } from '@tanstack/react-query';
import { updateCompany } from 'utils/api/companies';
import useAlertQueue from 'hooks/useAlertQueue';
import { type Company, CommerceType, type AttributeKey } from 'types/models/company';
import AttributesSelector from 'components/Common/AttributesSelector';
import Alert from 'storybookConfig/stories/cells/Alert';

interface FormFields {
  attributes: AttributeKey[];
}

const Attributes = () => {
  const company = useAppSelector(selectCompany);
  const companyId = useAppSelector(selectCompanyId);
  const { commerceType } = company ?? {};
  const { addErrorAlert, addSuccessAlert } = useAlertQueue();

  const { control, handleSubmit, getValues, setValue } = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: {
      attributes: company?.marketplaceSettings?.attributes || [],
    },
  });

  const attributes = useWatch({
    name: 'attributes',
    control,
  });

  const handleSelectAttribute = (key: AttributeKey) => {
    const currentAttributes = getValues('attributes');
    const updatedAttributes = currentAttributes.includes(key)
      ? currentAttributes.filter((attr) => attr !== key)
      : [...currentAttributes, key];

    setValue('attributes', updatedAttributes);
  };

  const { mutate: updateAttributes, isLoading } = useMutation<Company, Error, FormFields>({
    mutationFn: (data: FormFields) => {
      const updatedData = {
        attributes: data.attributes,
      };

      return updateCompany(companyId ?? '', {
        marketplaceSettings: updatedData,
      });
    },
    onSuccess: () => {
      addSuccessAlert('Attributes Updated', 'Your brand attributes have been saved successfully.');
    },
    onError: (error) => {
      addErrorAlert('Something went wrong', error.message);
      console.error('Unable to update attributes', error);
    },
  });

  const onSubmit = (data: FormFields) => {
    updateAttributes(data);
  };

  return (
    <SettingsMain>
      <SettingsPageHeader title="Attributes">
        <CommerceTypeAvatar commerceType={commerceType || CommerceType.None} />
      </SettingsPageHeader>
      <h4>Brand Identity & Values</h4>
      <p className="text-muted small" style={{ marginTop: '4px', marginBottom: '16px' }}>
        Select the attributes that best describe your brand identity and values.
      </p>
      {(attributes || []).length >= 3 && (
        <Alert type="info">
          We may review the items you add before they are published on your page.
        </Alert>
      )}
      <Form onSubmit={handleSubmit(onSubmit)} style={{ gap: '16px', marginTop: '24px' }}>
        <Form.FieldGroup>
          <AttributesSelector
            selectedAttributes={attributes || []}
            onAttributeClick={handleSelectAttribute}
          />
        </Form.FieldGroup>
        <Form.Footer justifyEnd>
          <PrimaryButton type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save'}
          </PrimaryButton>
        </Form.Footer>
      </Form>
    </SettingsMain>
  );
};

export default Attributes;
